// src/components/ExtensionUpdate.jsx — the extension download, offered from the Desk itself
// (owner 2026-09-29: "agents can download from desk directly instead of sending them every time").
//
// build.bat publishes every raw build to the server (server-new-libs/scripts/publish_extension.py);
// this file shows it in two places:
//   • ExtensionMenuItem — a permanent "download extension" row in the header user menu;
//   • ExtensionUpdateBanner — a banner when THIS browser's extension is older than the published
//     one, or when no extension answers on this page at all (desktop only — a phone cannot run it).
//
// ⚠️ Versions are compared by EQUALITY, never ordered. build.js stamps them from the clock as
// DDMMHHMM with no year, so ordering them would call a January build older than a December one.
// The published one is by definition the one agents should run, so "different" means "update".
// ⚠️ A ping that did NOT answer proves nothing about the version (a sleeping service worker, a
// tab orphaned by a reload — see lib/extensionBridge.js). That state never nags as "outdated".
import { useEffect, useState } from 'react'
import { t } from '../lib/i18n.js'
import { getExtensionLatest, fetchExtensionZip } from '../db/core.js'
import { pingExtension } from '../lib/extensionBridge.js'
import { Icon } from './Icon.jsx'

// One fetch and one ping per page load, shared by the menu row and the banner.
let _latest = null
let _ping = null
const loadLatest = () => (_latest ||= getExtensionLatest().catch(() => ({ version: null })))
const loadPing = () => (_ping ||= pingExtension().catch(() => ({ present: false, installed: false })))

// Phones and tablets cannot run a Chrome extension, so they are offered nothing (owner
// 2026-09-29: "no need to show that download on mobile"). An iPad reports itself as a Mac, so
// a touch Mac counts as a tablet.
const isDesktop = () => {
  try {
    const ua = navigator.userAgent || ''
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return false
    if (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1) return false
    return true
  } catch { return false }
}

function sameVersion(installed, latest) {
  if (!installed || !latest) return false
  return installed === latest.version_name || installed === latest.version
}

// Pure, so scripts/render-extension-update.mjs can assert every branch. `ext` is the ping
// answer (null off desktop); the returned ext.version is the build the banner names.
export function extensionStatus(latest, ext) {
  let status = 'unknown'
  let have = null
  if (ext?.present) { have = ext.version; status = sameVersion(have, latest) ? 'current' : 'outdated' }
  else if (ext && latest?.yours?.version && !sameVersion(latest.yours.version, latest)) { have = latest.yours.version; status = 'outdated' }
  else if (ext && !ext.installed && !ext.stale) status = 'missing'
  return { status, ext: ext ? { ...ext, version: have || ext.version || null } : ext }
}

// status: 'loading' | 'none' (nothing published) | 'current' | 'outdated' | 'missing' | 'unknown'
export function useExtensionStatus() {
  const [st, setSt] = useState({ status: 'loading', latest: null, ext: null })
  useEffect(() => {
    let alive = true
    ;(async () => {
      const latest = await loadLatest()
      if (!alive) return
      if (!latest?.version) { setSt({ status: 'none', latest: null, ext: null }); return }
      setSt(s => ({ ...s, latest }))
      const ext = isDesktop() ? await loadPing() : null
      if (!alive) return
      // A live answer from THIS browser wins. Without one, the server's record of the build this
      // agent's extension last reported (`latest.yours`, the heartbeat) still tells us they are
      // behind — which is exactly the agent whose old build cannot answer on app.sentavus.com.
      setSt({ latest, ...extensionStatus(latest, ext) })
    })()
    return () => { alive = false }
  }, [])
  return st
}

export async function downloadLatestExtension(latest) {
  const blob = await fetchExtensionZip()
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = `senTavus extension ${latest?.version_name || latest?.version || ''}.zip`.replace(/ \.zip$/, '.zip')
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 30000)
  }
}

function useDownload(latest) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [done, setDone] = useState(false)
  const run = async () => {
    if (busy) return
    setBusy(true); setErr(null)
    try { await downloadLatestExtension(latest); setDone(true) }
    catch { setErr(t('ext_dl_failed')) }
    finally { setBusy(false) }
  }
  return { busy, err, done, run }
}

// (status "outdated" may come from the heartbeat record; ext.version then carries THAT build.)
// Header user-menu row. Renders nothing until a version has been published.
export function ExtensionMenuItem() {
  const { status, latest } = useExtensionStatus()
  const dl = useDownload(latest)
  if (!isDesktop()) return null
  return <ExtensionMenuItemView status={status} latest={latest} dl={dl} />
}

// Pure view, so scripts/render-extension-update.mjs can draw every state without a network.
export function ExtensionMenuItemView({ status, latest, dl }) {
  if (!latest) return null
  return (
    <>
      <button onClick={dl.run} disabled={dl.busy}
        className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors disabled:opacity-60">
        ⬇ {dl.busy ? t('ext_dl_busy') : t('ext_dl_menu')}
        <span className="block text-[11px] text-muted-foreground">
          {t('ext_dl_version').replace('{v}', latest.version_name || latest.version)}
          {status === 'outdated' ? ` · ${t('ext_dl_newer')}` : status === 'current' ? ` · ${t('ext_dl_current')}` : ''}
        </span>
        {dl.err && <span className="block text-[11px] text-destructive">{dl.err}</span>}
      </button>
      <div className="border-t border-border/50 my-1" />
    </>
  )
}

// Header icon beside ♥ (owner 2026-09-29: "lets add download button here"). Always there on a
// desktop once a version is published — like ♥, an agent has to be able to find it to learn it
// exists. Owner: "just a download icon" — no label and no badge; whether this browser is behind
// is in the tooltip, and the banner is what asks.
export function ExtensionHeaderButton() {
  const { status, latest } = useExtensionStatus()
  const dl = useDownload(latest)
  if (!isDesktop()) return null
  return <ExtensionHeaderButtonView status={status} latest={latest} dl={dl} />
}

export function ExtensionHeaderButtonView({ status, latest, dl }) {
  if (!latest) return null
  const v = latest.version_name || latest.version
  const title = [t('ext_dl_menu'), t('ext_dl_version').replace('{v}', v),
                 status === 'outdated' ? t('ext_dl_newer') : status === 'current' ? t('ext_dl_current') : null,
                 dl.err].filter(Boolean).join(' · ')
  return (
    <button type="button" aria-label={t('ext_dl_menu')} title={title} onClick={dl.run} disabled={dl.busy}
      data-ext-header-button={status}
      className="relative w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-60">
      {dl.busy ? <span className="text-[15px] leading-none">…</span> : <Icon name="download" size={17} />}
    </button>
  )
}

const DISMISS_KEY = 'sntv-ext-update-dismissed'
function readDismissed() { try { return localStorage.getItem(DISMISS_KEY) } catch { return null } }
function writeDismissed(v) { try { localStorage.setItem(DISMISS_KEY, v) } catch { /* private mode */ } }

// Shell banner. "Later" hides it for THIS version only; the next published build asks again.
export function ExtensionUpdateBanner() {
  const { status, latest, ext } = useExtensionStatus()
  const dl = useDownload(latest)
  const [dismissed, setDismissed] = useState(readDismissed)
  const onLater = key => { writeDismissed(key); setDismissed(key) }
  if (!isDesktop()) return null
  return <ExtensionUpdateBannerView status={status} latest={latest} ext={ext} dl={dl}
                                    dismissed={dismissed} onLater={onLater} />
}

export function ExtensionUpdateBannerView({ status, latest, ext, dl, dismissed, onLater }) {
  if (!latest || (status !== 'outdated' && status !== 'missing')) return null
  const key = `${status}:${latest.version_name || latest.version}`
  if (dismissed === key) return null
  return (
    <div className="mb-3 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-800 dark:text-sky-200 flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="flex-1 min-w-[12rem]">
        🧩 {status === 'outdated'
          ? t('ext_banner_outdated').replace('{have}', ext?.version || '?').replace('{v}', latest.version_name || latest.version)
          : t('ext_banner_missing').replace('{v}', latest.version_name || latest.version)}
        {dl.done && <span className="block mt-0.5">{t('ext_dl_howto')}</span>}
        {dl.err && <span className="block mt-0.5 text-destructive">{dl.err}</span>}
      </span>
      <button onClick={dl.run} disabled={dl.busy}
        className="font-medium px-3 py-1 rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60">
        {dl.busy ? t('ext_dl_busy') : t('ext_dl_button')}
      </button>
      <button onClick={() => onLater(key)}
        className="underline text-sky-700 dark:text-sky-300">
        {t('ext_banner_later')}
      </button>
    </div>
  )
}
